import { IAction, KEEP_THE_SAME, StateStatus } from 'redux/utils/common';
import * as Types from './type';
import {
  IDocProposalDetailOutput,
  ICustomerMiniProfile,
} from 'services/api/docproposal';
import {
  IDocProposalSectionsDetailOutput,
  IDocProposalSectionsByDocIdOutput,
  IListTagsOutput,
  IListTagsWithSectionsOutput,
  IDocProposalSectionComment,
} from 'services/api/docproposalsections';
import { IDocProposalSectionPropertiesDetailOutput } from 'services/api/docproposalsectionproperties';
import { IDocProposalDropdownListOutput } from 'services/api/docproposaldropdowns';

interface IDataProperty {
  id: number;
  name: string;
}

export interface IState {
  documentDetail: {
    data: IDocProposalDetailOutput | null;
    status: StateStatus | null;
  };
  documentSectionByDocId: {
    data: IDocProposalSectionsByDocIdOutput['data'] | null; // list
    status: StateStatus | null;
  };
  documentPropertiesByDocId: {
    data: IDocProposalSectionPropertiesDetailOutput['data'][] | null;
    status: StateStatus | null;
  };
  propertyBySectionId: {
    data: IDocProposalSectionPropertiesDetailOutput['data'][] | null;
    status: StateStatus | null;
  };
  nodePathCurrent: {
    node: { title: string; id: number } | null;
    path: number[] | null;
  };
  createSection: StateStatus | null;
  deleteSection: StateStatus | null;
  sectionDetail: {
    // data: { [key: string]: IDocProposalSectionsDetailOutput['data'] } | null;
    data: IDocProposalSectionsDetailOutput['data'] | null;
    status: StateStatus | null;
  };
  editSection: StateStatus | null;
  updateSectionFull: StateStatus | null;
  propertyName: {
    status: StateStatus | null;
    data: IDataProperty[] | null;
  };
  createProperty: StateStatus | null;
  deleteProperty: StateStatus | null;
  treeData: any[];
  customers: {
    status: StateStatus | null;
    data: Array<ICustomerMiniProfile>;
  };
  assignProposalCustomers: StateStatus | null;
  exportDocx: StateStatus | null;
  cloneDocx: StateStatus | null;
  cloneSection: StateStatus | null;
  listTags: {
    data: IListTagsOutput[] | null;
    status: StateStatus | null;
  };
  listTagsWithSection: {
    data: IListTagsWithSectionsOutput[] | null;
    status: StateStatus | null;
  };
  sectionComments: {
    status: StateStatus | null;
    data: Array<IDocProposalSectionComment>;
  };
  insertSectionCommentStatus: StateStatus | null;
  listDropdowns: {
    data: IDocProposalDropdownListOutput | null;
    status: StateStatus | null;
  };
  addDropdown: StateStatus | null;
}

const initialState: IState = {
  documentDetail: {
    data: null,
    status: null,
  },
  documentSectionByDocId: {
    data: null,
    status: null,
  },
  documentPropertiesByDocId: {
    data: null,
    status: null,
  },
  propertyBySectionId: {
    data: null,
    status: null,
  },
  nodePathCurrent: {
    node: null,
    path: null,
  },
  createSection: null,
  deleteSection: null,
  sectionDetail: {
    data: null,
    status: null,
  },
  editSection: null,
  updateSectionFull: null,
  propertyName: {
    status: null,
    data: null,
  },
  createProperty: null,
  deleteProperty: null,
  treeData: [],
  customers: {
    data: [],
    status: null,
  },
  assignProposalCustomers: null,
  exportDocx: null,
  cloneDocx: null,
  cloneSection: null,
  listTags: {
    status: null,
    data: null,
  },
  listTagsWithSection: {
    status: null,
    data: null,
  },
  sectionComments: {
    status: null,
    data: [],
  },
  insertSectionCommentStatus: null,
  listDropdowns: {
    status: null,
    data: null,
  },
  addDropdown: null,
};

const DocumentEditReducer = (
  state: IState = initialState,
  action: IAction
): IState => {
  switch (action.type) {
    case Types.SET_DOCUMENT_DETAIL:
      return {
        ...state,
        documentDetail: {
          ...state.documentDetail,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.SET_DOCUMENT_SECTION_BY_DOC_ID:
      return {
        ...state,
        documentSectionByDocId: {
          ...state.documentSectionByDocId,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.SET_DOCUMENT_PROPERTIES_BY_DOC_ID:
      return {
        ...state,
        documentPropertiesByDocId: {
          ...state.documentPropertiesByDocId,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.SET_PROPERTY_BY_SECTION_ID:
      return {
        ...state,
        propertyBySectionId: {
          ...state.propertyBySectionId,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.SET_NODE_PATH_CURRENT:
      return {
        ...state,
        nodePathCurrent: action.data,
      };
    case Types.SET_ADD_SECTION:
      return {
        ...state,
        createSection: action.status || null,
      };
    case Types.SET_DELETE_SECTION:
      return {
        ...state,
        deleteSection: action.status || null,
      };
    case Types.SET_SECTION_DETAIL:
      return {
        ...state,
        sectionDetail: {
          ...state.sectionDetail,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.SET_EDIT_SECTION:
      return {
        ...state,
        editSection: action.status || null,
      };
    case Types.SET_UPDATE_FULL_SECTION:
      return {
        ...state,
        updateSectionFull: action.status || null,
      };
    case Types.SET_PROPERTY_NAME:
      return {
        ...state,
        propertyName: {
          ...state.propertyName,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.SET_ADD_PROPERTY:
      return {
        ...state,
        createProperty: action.status || null,
      };
    case Types.SET_DELETE_PROPERTY:
      return {
        ...state,
        deleteProperty: action.status || null,
      };
    case Types.SET_TREE_DATA:
      return {
        ...state,
        treeData: action.data,
      };
    case Types.SET_CUSTOMERS:
      return {
        ...state,
        customers: {
          ...state.customers,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.ASSIGN_CUSTOMERS:
      return {
        ...state,
        assignProposalCustomers: action.status || null,
      };
    case Types.SET_EXPORT_DOCX:
      return {
        ...state,
        exportDocx: action.status || null,
      };
    case Types.SET_CLONE_DOCX:
      return {
        ...state,
        cloneDocx: action.status || null,
      };
    case Types.SET_CLONE_SECTION:
      return {
        ...state,
        cloneSection: action.status || null,
      };
    case Types.SET_LIST_TAGS:
      return {
        ...state,
        listTags: {
          ...state.listTags,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.SET_LIST_TAGS_WITH_SECTION:
      return {
        ...state,
        listTagsWithSection: {
          ...state.listTagsWithSection,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.INSERT_SECTION_COMMENT:
      return {
        ...state,
        insertSectionCommentStatus: action.status || null,
      };
    case Types.SET_SECTION_COMMENTS:
      return {
        ...state,
        sectionComments: {
          ...state.sectionComments,
          status: action.status || null,
          data:
            action.data === KEEP_THE_SAME
              ? state.sectionComments.data
              : action.data,
        },
      };
    case Types.SET_LIST_DROPDOWNS:
      return {
        ...state,
        listDropdowns: {
          ...state.listDropdowns,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.SET_ADD_DROPDOWN:
      return {
        ...state,
        addDropdown: action.status || null,
      };
    default:
      return {
        ...state,
      };
  }
};

export default DocumentEditReducer;
